/**
 * @fileoverview Marco de las pantallas de acceso: columna de marca, columna de contenido y aviso de privacidad.
 * @module AuthLayout
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldHalved } from '@fortawesome/free-solid-svg-icons';

interface AuthLayoutProps {
  children: React.ReactNode;
}

/**
 * @component AuthLayout
 * @description Marco compartido por el login y las pantallas que deben verse igual que el: fondo,
 * columna izquierda con el logo de la marca (solo en pantallas grandes), columna derecha que centra
 * el contenido y pie con el aviso de privacidad.
 * @param {object} props Las propiedades del componente.
 * @param {React.ReactNode} props.children Contenido de la columna derecha (la tarjeta de la pantalla).
 * @returns {React.ReactElement} El componente renderizado.
 */
export const AuthLayout: React.FC<AuthLayoutProps> = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col bg-background-primary">
      <div className="flex-1 flex min-h-0">
        {/* Seccion izquierda con imagen */}
        <div className="hidden lg:flex lg:w-1/2 items-center justify-center bg-gradient-to-br p-4">
          <div className="w-full ">
            <img
              src="/LOGOSRFACIL_CONSLOGAN.svg"
              alt="Rfacil Materialidad"
              className="w-full h-auto object-contain max-w-md mx-auto"
            />
          </div>
        </div>

        {/* Seccion derecha con el contenido de la pantalla */}
        <div className="w-full lg:w-1/2 flex items-center justify-center p-5">
          {children}
        </div>
      </div>

      <footer
        className="shrink-0 bg-gradient-to-b from-host-50/50 to-background-primary py-5 px-4 sm:px-8"
        role="contentinfo"
        aria-label="Aviso de privacidad"
      >
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
          <div
            className="flex justify-center sm:justify-start shrink-0"
            aria-hidden
          >
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-host-100 text-host-600 shadow-sm ring-1 ring-host-200/50">
              <FontAwesomeIcon icon={faShieldHalved} className="text-lg" />
            </span>
          </div>
          <div className="text-center sm:text-left space-y-1.5">
            <p className="text-sm font-semibold text-gray-800">
              Privacidad y protección de datos
            </p>
            <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
              La información que nos proporciones se utiliza solo para gestión y comunicación
              relacionada con el servicio.{' '}

              <a
                href="https://rfacil.com.mx/aviso-de-privacidad/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-host-600 font-medium underline decoration-host-300 underline-offset-2 hover:text-host-700 hover:decoration-host-500 transition-colors"
              >
                Consulta nuestro Aviso de privacidad
              </a>
              .
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AuthLayout;
